import axios from 'axios'
import Echo from 'laravel-echo'
import Pusher from 'pusher-js'

window.axios = axios
window.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'

// Environment detection
const isProd = import.meta.env.MODE === 'production'
const isSecure = window.location.protocol === 'https:'

// Configuration variables with environment-aware defaults
// In development: use window.location.hostname
// In production: use the VITE_PUSHER_HOST from environment if available, falling back to window.location.hostname
const wsHost = import.meta.env.VITE_PUSHER_HOST || window.location.hostname

// Default port from env vars or fallback to 6002 as specified in the project configuration
const wsPort = parseInt(import.meta.env.VITE_PUSHER_PORT || '6002')

// Use window location port for production with secure connections (usually 443)
const wssPort = isSecure ? (window.location.port || 443) : wsPort

// Force TLS in secure contexts
const forceTLS = isSecure
const encrypted = isSecure
const pusherKey = import.meta.env.VITE_PUSHER_APP_KEY || '27436832e2f0d4e4b4e4b4e'
const cluster = import.meta.env.VITE_PUSHER_APP_CLUSTER || 'mt1'

// Use https scheme when secure, otherwise http
const scheme = isSecure ? 'https' : 'http'
// Initialize Echo for WebSocket communication
window.Echo = new Echo({
  broadcaster: 'pusher',
  key: pusherKey,
  wsHost: wsHost,
  cluster: cluster,
  wsPort: wsPort,
  wssPort: wssPort,
  forceTLS: forceTLS,
  encrypted: encrypted,
  disableStats: true,
  enabledTransports: ['ws', 'wss'],
  namespace: '',
  
  // Give the connection more time on production
  activityTimeout: isProd ? 60000 : 30000,
  pongTimeout: isProd ? 30000 : 15000,
  
  authEndpoint: '/broadcasting/auth', // keep if you're using private channels
  auth: {
    headers: {
      'Authorization': 'Bearer ' + (localStorage.getItem('accessToken') || ''),
      'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
    },
  },
})


/**
 * Handler for ParcelUpdated events
 */
const handleParcelUpdated = e => {
  // Dispatch the event
  window.dispatchEvent(new CustomEvent('parcel:updated', { detail: e }))

  // Create notification for status updates
  if (e.parcel) {
    createNotification({
      title: 'Parcel Updated',
      message: `Parcel ${e.parcel.barcode || 'unknown'} was updated`,
      parcel_id: e.parcel.id,
      type: 'parcel_updated',
      title_key: 'notifications.parcel_updated',
      message_key: 'notifications.parcel_updated_message',
      message_data: {
        barcode: e.parcel.barcode || 'unknown',
        status: e.parcel.status || '',
      },
    })
  }
}

/**
 * Handler for ParcelCreated events (from mobile app)
 */
const handleParcelCreated = e => {
  // Create notification - handle both parcel object structures
  const parcelBarcode = e.parcel?.barcode || (e.parcel ? e.parcel : 'unknown')
  const parcelId = e.parcel?.id || (e.parcel ? e.parcel.id : null)

  // Dispatch entity-specific event
  window.dispatchEvent(new CustomEvent('parcel:created', { detail: e }))

  // Create standard notification with localization support
  createNotification({
    title: 'New Parcel',
    message: `New parcel ${parcelBarcode} created`,
    parcel_id: parcelId,
    type: 'parcel_created',
    title_key: 'notifications.new_parcel',
    message_key: 'notifications.parcel_created_message',
    message_data: {
      barcode: parcelBarcode,
    },
  })
}

/**
 * Handler for incoming parcel created events
 */
const handleIncomingParcelCreated = e => {
  // Skip notifications during bulk import
  if (window.__bulkImportInProgress) {
    return
  }

  // Dispatch entity-specific event
  window.dispatchEvent(new CustomEvent('parcel:created', { detail: e }))

  // Create standard notification with localization support
  createNotification({
    title: 'New Parcel',
    message: `New parcel ${e.parcel?.barcode || 'unknown'} created`,
    parcel_id: e.parcel?.id,
    type: 'parcel_created',
    title_key: 'notifications.new_parcel',
    message_key: 'notifications.parcel_created_message',
    message_data: {
      barcode: e.parcel?.barcode || 'unknown',
    },
  })
}

/**
 * Helper function to create and dispatch a notification
 */
const createNotification = data => {
  // Note: Skip creating client-side notifications with timestamp IDs to prevent warnings
  // Server-side notifications with proper UUIDs should handle persistent notifications
  // This function now only dispatches custom events for immediate UI feedback
  
  // Just dispatch entity-specific events for immediate UI feedback (like toasts)
  // The actual persistent notification should come from the server with a proper UUID
  console.log('Bootstrap notification event processed for immediate feedback:', data.type, data.title)
}

// Create our parcel channel subscription explicitly
const parcelsChannel = window.Echo.channel('parcels')

// Make explicit bindings to ensure they're registered
parcelsChannel

  // Incoming Parcel created events from mobile app
  // Listen for both full namespaced and simplified event names
  .listen('Modules\\Shipment\\Events\\IncomingParcelCreated', e => {
    handleIncomingParcelCreated(e)
  })

  // Also listen for the event with the broadcastAs name
  .listen('IncomingParcelCreated', e => {
    handleIncomingParcelCreated(e)
  })

  // Listen for ParcelCreated events with both formats
  .listen('Modules\\Shipment\\Events\\ParcelCreated', e => {
    handleParcelCreated(e)
  })

  // Also listen with broadcastAs name
  .listen('ParcelCreated', e => {
    handleParcelCreated(e)
  })

  // Parcel updated events - support both formats
  .listen('Modules\\Shipment\\Events\\ParcelUpdated', e => {
    handleParcelUpdated(e)
  })

  // Also listen with broadcastAs name
  .listen('ParcelUpdated', e => {
    handleParcelUpdated(e)
  })

  // Parcel status updated events
  .listen('Modules\\Shipment\\Events\\ParcelStatusUpdated', e => {
    window.dispatchEvent(new CustomEvent('parcel:status-updated', { detail: e }))

    // Create notification for status change
    if (e.parcel) {
      createNotification({
        title: 'Parcel Status Updated',
        message: `Parcel ${e.parcel?.barcode || 'unknown'} status changed to ${e.parcel?.status || 'unknown'}`,
        parcel_id: e.parcel?.id,
        type: 'parcel_status_updated',
        title_key: 'notifications.parcel_status_updated',
        message_key: 'notifications.parcel_status_updated_message',
        message_data: {
          barcode: e.parcel?.barcode || 'unknown',
          status: e.parcel?.status || 'unknown',
        },
      })
    }
  })

// Create our packages channel subscription explicitly
const packagesChannel = window.Echo.channel('packages')

// Make explicit bindings to ensure they're registered
packagesChannel

  // Package created events
  .listen('Modules\\Shipment\\Events\\PackageCreated', e => {
    // Dispatch entity-specific event
    window.dispatchEvent(new CustomEvent('package:created', { detail: e }))

    // Create standard notification with localization support
    createNotification({
      title: 'New Package',
      message: `New package ${e.package?.barcode || e.package?.sku || 'unknown'} created`,
      package_id: e.package?.id,
      type: 'package_created',
      title_key: 'notifications.new_package',
      message_key: 'notifications.package_created_message',
      message_data: {
        barcode: e.package?.barcode || e.package?.sku || 'unknown',
      },
    })
  })

  // Package updated events
  .listen('Modules\\Shipment\\Events\\PackageUpdated', e => {
    window.dispatchEvent(new CustomEvent('package:updated', { detail: e }))
  })

// WebSocket connection management
window.Echo.connector.pusher.connection.bind('connected', () => {
  // Verify channel subscriptions
  const channels = window.Echo.connector.pusher.channels.channels
  const channelNames = Object.keys(channels)

  // Only create a new channel if it doesn't exist
  if (!channelNames.includes('parcels')) {
    // The channel reference is already globally available as 'parcelsChannel'
    // No need to create new event handlers as they're already set up
  }
})

// Connection handling
let reconnectAttempts = 0
const maxReconnectAttempts = 5

// Handle successful connections
window.Echo.connector.pusher.connection.bind('connected', () => {
  
  // Reset reconnect attempts counter on successful connection
  reconnectAttempts = 0
  
  // Ensure channels are subscribed
  const channels = window.Echo.connector.pusher.channels.channels
  const channelNames = Object.keys(channels)
  
  // Make sure we have our parcels channel
  if (!channelNames.includes('parcels')) {
    window.Echo.channel('parcels')
  }
})

// Handle connection initialization
window.Echo.connector.pusher.connection.bind('connecting', () => {
  console.info('WebSocket connecting...', {
    attempt: reconnectAttempts + 1,
    host: wsHost,
    port: wsPort,
    path: '/app/' + pusherKey,
  })
})

// Handle WebSocket errors
window.Echo.connector.pusher.connection.bind('error', error => {
  console.error('WebSocket connection error:', error, {
    host: wsHost,
    port: wsPort,
    state: window.Echo.connector.pusher.connection.state,
    attempt: reconnectAttempts + 1,
    maxAttempts: maxReconnectAttempts,
  })
  
  // Try to reconnect if we haven't exceeded max attempts
  if (reconnectAttempts < maxReconnectAttempts) {
    reconnectAttempts++

    const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000) // Exponential backoff up to 30 seconds

    console.info(`Reconnecting in ${delay/1000} seconds...`)
    
    setTimeout(() => {
      // Try reconnecting
      window.Echo.connector.pusher.connect()
    }, delay)
  } else {
    console.error('Maximum reconnection attempts reached. WebSocket connection failed permanently.')
  }
})

// Handle failed connection attempts
window.Echo.connector.pusher.connection.bind('failed', () => {
  console.error('WebSocket connection failed after all retries', {
    host: wsHost,
    port: wsPort,
  })
})

// Handle disconnections
window.Echo.connector.pusher.connection.bind('disconnected', () => {
  console.warn('WebSocket disconnected', {
    attempt: reconnectAttempts + 1,
    maxAttempts: maxReconnectAttempts,
  })
  
  // Try to reconnect if we haven't exceeded max attempts
  if (reconnectAttempts < maxReconnectAttempts) {
    reconnectAttempts++

    const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000)

    console.info(`Reconnecting in ${delay/1000} seconds...`)
    
    setTimeout(() => {
      window.Echo.connector.pusher.connect()
    }, delay)
  } else {
    console.error('Maximum reconnection attempts reached after disconnect.')
  }
})

// Add handler for unsupported feature
window.Echo.connector.pusher.connection.bind('unsupported', () => {
  console.error('WebSocket connection unsupported by the browser or server')
})
